import { useEffect, useMemo, useRef, useState } from "react";
import { useFavoriteExercise, useGetExercisesListMutation } from "../hooks/useGetDataQuery";
import { BlockUI } from "primereact/blockui";
import { useOutletContext } from "react-router-dom";
import "./library.css";
import ExerciseVideoDialog from "./exerciseVideoDialog";
import LibraryHeaderContent from "./libraryHeaderContent";
import { useDebounce } from "../hooks/useDebounce";
import LibrarySearchFilterSection from "./librarySearchFilterSection";
import LibraryContent from "./libraryContent";
import PanelCreateDialog from "./PanelCreateDialog/panelCreateDialog";

const Library = () => {
    const { t, i18n }: any = useOutletContext();
    const { myPanels }: any = useOutletContext();
    const { setMyPanel }: any = useOutletContext();
    const { systemPanels }: any = useOutletContext();

    const [blockTable, setBlockTable] = useState(true);
    const [searchValue, setSearchValue] = useState("");
    const debouncedValue = useDebounce(searchValue);

    const [selectedExercises, setSelectedExercises] = useState<any>([]);
    const [selectedPanelsId, setSelectedPanelsId] = useState<any>([]);
    const [selectedBodyParts, setSelectedBodyParts] = useState<any[]>([]);
    const [selectedEquipments, setSelectedEquipments] = useState<any[]>([]);

    const [addedExercises, setAddedExercises] = useState<any>([]);

    const [onlyFavorites, setOnlyFavorites] = useState(false);

    const [panelCreateDialogVisibility, setPanelCreateDialogVisibility] = useState(false);

    const [searchFilterSectionVisibility, setSearchFilterSectionVisibility] = useState(true);

    const [lazyParamsForExercises, setLazyParamsForExercises] = useState<any>({
        first: 0,
        rows: 200,
        page: 0,
        sortField: "name",
        sortOrder: 1,
        filters: {
            name: {
                matchMode: "contains",
                value: "",
            },
        },
    });

    const [exerciseProcedureList, setExerciseProcedureList] = useState<any>([]);

    const [exerciseVideoDialogVisible, setExerciseVideoDialogVisible] = useState(false);
    const [selectedExercise, setSelectedExercise] = useState<any>({});

    const cartIconRef = useRef<any>(null);

    const { data: exercisesData, mutate: getExercisesList, isSuccess: isSuccessExerciseData, isError: isErrorExerciseData, isPending: isLoadingExerciseData } =
        useGetExercisesListMutation();

    useEffect(() => {
        if (lazyParamsForExercises) {
            getExercisesList(lazyParamsForExercises);
        }
    }, [lazyParamsForExercises, i18n.language]);

    const {
        mutate: addFavoriteExercise,
        data: favoritesData,
        isSuccess: isSuccessFavoritesData,
    } = useFavoriteExercise();

    useEffect(() => {
        if (isSuccessExerciseData) {
            const purifiedData = exercisesData?.data.map((item: any) => {
                return {
                    id: item.id,
                    name: item.name,
                    image: item.imageUrl || "/exercise-image.svg",
                    videoUrl: item.videoUrl,
                    days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
                    set: 4,
                    repeat: 12,
                    tolerance: 5,
                    restInterval: 12 / 3,
                    isFavorite: item.isFavorite,
                    bodyParts: item.bodyParts,
                    equipments: item.equipments,
                };
            });
            setExerciseProcedureList(purifiedData);
            if (addedExercises?.length > 0) {
                setAddedExercises((prev: any[]) => prev?.map((exercise: any) => {
                    const newExercise = purifiedData?.find((item: any) => item.id === exercise.id);
                    return {
                        ...exercise,
                        name: newExercise ? newExercise.name : exercise.name
                    }
                }))
            }
            setBlockTable(false)
        }
    }, [isSuccessExerciseData, exercisesData]);

    useEffect(() => {
        if (isLoadingExerciseData) {
            setBlockTable(true)
        }
    }, [isLoadingExerciseData]);


    useEffect(() => {
        if (isErrorExerciseData) {
            setBlockTable(false)
        }
    }, [isErrorExerciseData]);

    const purifyData = (data: any) => {
        return data.map((item: any) => {
            return {
                id: item.procedureId,
                name: item.exerciseName,
                image: item.exerciseImg || "/exercise-image.svg",
                ...item
            };
        });
    }

    const normalize = (str: string) =>
        str.toLowerCase().replace(/\s+/g, "").trim();

    const filteredExercises = useMemo(() => {
        let list = selectedPanelsId.length > 0 ? selectedExercises : exerciseProcedureList;
        if (debouncedValue) {
            list = list.filter((exercise: any) => {
                return exercise.name.toLowerCase().includes(debouncedValue.toLowerCase());
            });
        }
        if (onlyFavorites) {
            list = list.filter((exercise: any) => exercise?.isFavorite);
        }
        if (selectedBodyParts.length > 0) {
            list = list.filter((exercise: any) => {
                return selectedBodyParts.some((selected: any) =>
                    exercise?.bodyParts?.some((bp: any) => normalize(bp.name) === normalize(selected.name))
                );
            });
        }
        if (selectedEquipments.length > 0) {
            list = list.filter((exercise: any) => {
                return selectedEquipments.some((selected: any) =>
                    exercise?.equipments?.some((eq: any) => normalize(eq.name) === normalize(selected.name))
                );
            });
        }
        return list;
    }, [selectedPanelsId, selectedExercises, exerciseProcedureList, debouncedValue, selectedBodyParts, selectedEquipments, onlyFavorites, t])

    const handleSelectPanel = (panel: any) => {
        const selectedIndex = selectedPanelsId.findIndex((item: any) => item === panel.id);
        if (selectedIndex > -1) {
            const updatedSelectedPanels = selectedPanelsId.filter((item: any) => item !== panel.id);
            setSelectedPanelsId(updatedSelectedPanels);
            const updatedSelectedPanelsExercises = [...myPanels, ...systemPanels].filter((item: any) => {
                return updatedSelectedPanels.includes(item.id);
            });
            const remainingExercises = updatedSelectedPanelsExercises.reduce((acc: any, current: any) => {
                return [...acc, ...purifyData(current.exerciseProcedure)];
            }, []);
            setSelectedExercises(remainingExercises);
        } else {
            setSelectedPanelsId((prev: any) => [...prev, panel.id]);
            const filteredExercises = exerciseProcedureList.filter((exercise: any) => {
                return panel.exerciseProcedure.some((item: any) => item.procedureId === exercise.id);
            });
            setSelectedExercises((prevSelectedExercises: any) => {
                const exercises = filteredExercises.filter(
                    (exercise: any) => !prevSelectedExercises.some((selected: any) => selected.id === exercise.id)
                );
                return [...prevSelectedExercises, ...exercises];
            });
        }
    }
    const handleAdd = (exercise: any, index: number, e: any) => {
        const productCard = e.currentTarget.closest(".product-card");
        const addButton = productCard.querySelector(`#add-button-${index}`);
        addButton.disabled = true;
        const productImg = productCard.querySelector(".exercise-img");
        const altDiv = productCard.children[0] as HTMLElement;
        altDiv.classList.add("clickable-data-div");
        const img = productCard.getElementsByTagName("img");
        img[img.length - 1].src = "/exerciseCircleIconOnHover.png";

        const imgClone = productImg.cloneNode(true);
        imgClone.classList.add("fly-img");

        const rect = productImg.getBoundingClientRect();
        const verticalOffset = window.innerHeight * (20 / 1080);
        const horizontalOffset = window.innerWidth * (300 / 1920);

        imgClone.style.top = `${rect.top + verticalOffset}px`;
        imgClone.style.left = `${rect.left - horizontalOffset}px`;
        document.body.appendChild(imgClone);

        const cartRect = cartIconRef.current.getBoundingClientRect();

        requestAnimationFrame(() => {
            imgClone.style.transform = `translate(${cartRect.left - rect.left}px, ${cartRect.top - rect.top - 100}px) scale(0.2)`;
            imgClone.style.opacity = "0";
        });

        imgClone.addEventListener("transitionend", (event: any) => {
            if (event.propertyName === "transform") {
                imgClone.remove();
                setAddedExercises((prev: any) => [...prev, exercise]);
            }
        });
    };

    const handleExerciseDelete = (exercise: any) => {
        setAddedExercises(addedExercises.filter((added: any) => added.id !== exercise.id));
    }

    const handleFavorite = (exercise: any, status: string) => {
        addFavoriteExercise({
            status: status,
            favoriteExerciseId: exercise.id,
        })
    }

    useEffect(() => {
        if (isSuccessFavoritesData) {
            if (favoritesData?.success) {
                const exerciseId = favoritesData?.data;
                const updateFavoriteStatus = (exercises: any[]) =>
                    exercises.map((exercise: any) =>
                        exercise.id === exerciseId
                            ? { ...exercise, isFavorite: !exercise.isFavorite }
                            : exercise
                    );

                setExerciseProcedureList(updateFavoriteStatus);
                setSelectedExercises(updateFavoriteStatus);
            }
        }
    }, [isSuccessFavoritesData])

    const contentExercisesIsEmpty = () => {
        return exerciseProcedureList?.length === 0 && !blockTable;
    }

    return (
        <>
            <div>
                <LibraryHeaderContent
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    addedExercisesLength={addedExercises?.length}
                    setPanelCreateDialogVisibility={setPanelCreateDialogVisibility}
                    searchFilterSectionVisibility={searchFilterSectionVisibility}
                    setSearchFilterSectionVisibility={setSearchFilterSectionVisibility}
                    contentExercisesIsEmpty={contentExercisesIsEmpty}
                    cartIconRef={cartIconRef}
                    t={t} />
                <div className="grid h-screen">
                    {searchFilterSectionVisibility && (
                        <div className="col-3 h-full overflow-y-auto overflow-x-hidden">
                            <LibrarySearchFilterSection
                                onlyFavorites={onlyFavorites}
                                setOnlyFavorites={setOnlyFavorites}
                                systemPanels={[...myPanels, ...systemPanels]}
                                handleSelectPanel={handleSelectPanel}
                                selectedPanelsId={selectedPanelsId}
                                setSearchFilterSectionVisibility={setSearchFilterSectionVisibility}
                                selectedBodyParts={selectedBodyParts}
                                setSelectedBodyParts={setSelectedBodyParts}
                                selectedEquipments={selectedEquipments}
                                setSelectedEquipments={setSelectedEquipments}
                                t={t} />
                        </div>
                    )}
                    <div className={`${searchFilterSectionVisibility ? "col-9" : "col-12"} h-full overflow-y-auto overflow-x-hidden`}>
                        <BlockUI
                            blocked={blockTable}
                            style={{ backgroundColor: "transparent", borderRadius: "24px" }}
                            className="h-screen"
                            template={
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <dotlottie-player
                                        src="https://lottie.host/102dcba8-b9a9-447a-af8e-3e134e77ea0c/Esb1U6xLam.lottie"
                                        background="transparent"
                                        speed="1"
                                        style={{ width: "3rem", height: "3rem" }}
                                        loop
                                        autoplay
                                    />
                                </div>
                            }
                        >
                            <LibraryContent searchFilterSectionVisibility={searchFilterSectionVisibility} exercises={filteredExercises} handleExerciseDelete={handleExerciseDelete} addedExercises={addedExercises} handleAdd={handleAdd} setExerciseVideoDialogVisible={setExerciseVideoDialogVisible} setSelectedExercise={setSelectedExercise} t={t} handleFavorite={handleFavorite} cartIconRef={cartIconRef} />
                        </BlockUI>
                    </div>
                </div>
            </div>
            <PanelCreateDialog
                panelCreateDialogVisibility={panelCreateDialogVisibility}
                setPanelCreateDialogVisibility={setPanelCreateDialogVisibility}
                addedExercises={addedExercises}
                t={t}
                myPanels={myPanels}
                setMyPanel={setMyPanel}
                setAddedExercises={setAddedExercises}
            />
            <ExerciseVideoDialog
                exerciseVideoDialogVisible={exerciseVideoDialogVisible}
                setExerciseVideoDialogVisible={setExerciseVideoDialogVisible}
                selectedExercise={selectedExercise} />

        </ >
    )
}

export default Library